<template>
  <div>
    <div class="d-flex align-center mb-6">
      <v-icon icon="mdi-view-dashboard" color="primary" size="32" class="mr-3" />
      <div>
        <h1 class="text-h5 font-weight-bold">Dashboard</h1>
        <p class="text-body-2 text-medium-emphasis">Opus Reference Architecture Template</p>
      </div>
    </div>

    <!-- Stats Row -->
    <v-row class="mb-6">
      <v-col cols="12" sm="6" md="4">
        <v-card rounded="lg">
          <v-card-text class="d-flex align-center pa-5">
            <v-avatar color="primary" size="52" class="mr-4">
              <v-icon icon="mdi-folder-multiple" size="28" color="white" />
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ loading ? '—' : programCount }}</div>
              <div class="text-body-2 text-medium-emphasis">Programs</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card rounded="lg">
          <v-card-text class="d-flex align-center pa-5">
            <v-avatar color="secondary" size="52" class="mr-4">
              <v-icon icon="mdi-file-tree" size="28" color="white" />
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ loading ? '—' : domainCount }}</div>
              <div class="text-body-2 text-medium-emphasis">Template Domains</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" sm="6" md="4">
        <v-card rounded="lg">
          <v-card-text class="d-flex align-center pa-5">
            <v-avatar color="info" size="52" class="mr-4">
              <v-icon icon="mdi-format-list-bulleted" size="28" color="white" />
            </v-avatar>
            <div>
              <div class="text-h4 font-weight-bold">{{ loading ? '—' : fieldCount }}</div>
              <div class="text-body-2 text-medium-emphasis">Template Fields</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Quick Actions -->
    <v-row>
      <v-col cols="12" md="6">
        <v-card rounded="lg" height="100%">
          <v-card-title class="pa-5 pb-3">
            <v-icon icon="mdi-lightning-bolt" color="accent" class="mr-2" />
            Quick Actions
          </v-card-title>
          <v-divider />
          <v-list nav>
            <v-list-item
              prepend-icon="mdi-folder-plus"
              title="Create New Program"
              subtitle="Set up a new architecture program"
              to="/programs"
              rounded="lg"
              active-color="primary"
            >
              <template #append>
                <v-icon icon="mdi-chevron-right" />
              </template>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-file-tree"
              title="Browse Template"
              subtitle="View all domains and fields"
              to="/template"
              rounded="lg"
              active-color="primary"
            >
              <template #append>
                <v-icon icon="mdi-chevron-right" />
              </template>
            </v-list-item>
            <v-list-item
              prepend-icon="mdi-folder-multiple"
              title="View All Programs"
              subtitle="Browse and manage programs"
              to="/programs"
              rounded="lg"
              active-color="primary"
            >
              <template #append>
                <v-icon icon="mdi-chevron-right" />
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card rounded="lg" height="100%">
          <v-card-title class="pa-5 pb-3">
            <v-icon icon="mdi-information" color="info" class="mr-2" />
            About Opus RAT
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-5">
            <p class="text-body-2 mb-3">
              The <strong>Reference Architecture Template (RAT)</strong> provides a standardized
              framework for documenting and governing architecture decisions across programs.
            </p>
            <p class="text-body-2 mb-3">
              Each program captures field values across all template domains. Fields that deviate
              from the template defaults are flagged and may require justification.
            </p>
            <v-alert
              type="info"
              variant="tonal"
              density="compact"
              class="mt-3"
              icon="mdi-lightbulb"
            >
              Start by browsing the <strong>Template</strong> to understand the available fields,
              then create a <strong>Program</strong> to begin capturing values.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recently Edited Programs -->
    <v-card rounded="lg" class="mt-6">
      <v-card-title class="pa-5 pb-3">
        <v-icon icon="mdi-history" color="primary" class="mr-2" />
        Recently Edited Programs
      </v-card-title>
      <v-divider />
      <v-data-table
        :items="recentPrograms"
        :headers="recentHeaders"
        :loading="loading"
        hover
        :items-per-page="-1"
        hide-default-footer
        @click:row="onRowClick"
      >
        <template #item.name="{ item }">
          <span class="font-weight-medium text-primary" style="cursor: pointer">{{ item.name }}</span>
        </template>

        <template #item.updatedAt="{ item }">
          <span class="text-body-2">{{ formatDate(item.updatedAt) }}</span>
        </template>

        <template #item.updatedBy="{ item }">
          <span class="text-body-2">{{ formatUserName(item.updatedBy) }}</span>
        </template>

        <template #item.status="{ item }">
          <v-chip
            :color="statusColor(item.status)"
            size="small"
            variant="tonal"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
        </template>

        <template #item.actions="{ item }">
          <v-btn
            icon="mdi-pencil"
            size="small"
            variant="text"
            color="primary"
            :to="`/programs/${item.id}`"
            @click.stop
          />
        </template>

        <template #no-data>
          <div class="pa-6 text-center">
            <p class="text-body-2 text-medium-emphasis">No programs yet</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Error snackbar -->
    <v-snackbar v-model="error" color="error" timeout="5000">
      {{ errorMessage }}
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { templateApi, programApi } from '@/api/client'

const router = useRouter()

const loading = ref(true)
const programCount = ref(0)
const domainCount = ref(0)
const fieldCount = ref(0)
const recentPrograms = ref<any[]>([])
const error = ref(false)
const errorMessage = ref('')

const recentHeaders = [
  { title: 'Name', key: 'name', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Updated', key: 'updatedAt', sortable: false },
  { title: 'Last Edited By', key: 'updatedBy', sortable: false },
  { title: '', key: 'actions', sortable: false, align: 'end' as const },
]

const statusMap: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'grey' },
  in_review: { label: 'In Review', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  archived: { label: 'Archived', color: 'error' },
}

function statusLabel(s: string): string { return statusMap[s]?.label || s }
function statusColor(s: string): string { return statusMap[s]?.color || 'grey' }

function formatDate(iso: string): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

function formatUserName(email: string): string {
  if (!email || email === 'system') return '—'
  const local = email.split('@')[0]
  return local.replace(/\./g, ' ')
}

function onRowClick(_e: Event, row: { item: any }) {
  router.push(`/programs/${row.item.id}`)
}

onMounted(async () => {
  try {
    const [programs, template] = await Promise.all([
      programApi.list(),
      templateApi.getCurrent(),
    ])
    programCount.value = programs.length

    // Top 10 most recently edited
    recentPrograms.value = programs
      .sort((a: any, b: any) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 10)

    // Count domains + fields (including sub-domains)
    let domains = 0
    let fields = 0
    const countDomains = (domainList: any[]) => {
      for (const d of domainList) {
        domains++
        fields += d.fields?.length ?? 0
        if (d.subDomains?.length) countDomains(d.subDomains)
      }
    }
    countDomains(template.domains)
    domainCount.value = domains
    fieldCount.value = fields
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Failed to load dashboard data'
    error.value = true
  } finally {
    loading.value = false
  }
})
</script>
